"use client"

import { useEffect } from "react"
import { AlertTriangle, RefreshCw, Home, LogOut, LogIn } from "lucide-react"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import { captureException } from "@/lib/telemetry-client"

/**
 * Shared route-level error boundary UI.
 *
 * Every `app/**\/error.tsx` in this repo was a hand-copied variant of the same
 * card — 19 files, 1,187 lines, three visual dialects and two different
 * policies on what to show staff. They drifted the way copy-pasted files do:
 * three of them still exported `AdminError` on routes that were not admin, and
 * the "never render raw error internals" convention below was written down in
 * exactly two of the nineteen.
 *
 * `app/global-error.tsx` deliberately does NOT use this — it replaces the root
 * layout and must render its own <html>/<body>, so it stays standalone.
 */

/** Secondary button offered next to "Try Again". */
export type RouteErrorAction =
  /** Back to `/` — public/unauthenticated surfaces. */
  | "home"
  /** POST `/api/auth/signout` — staff surfaces behind a PIN session. */
  | "signout"
  /** Back to `/login` — admin-class surfaces. */
  | "login"

export interface RouteErrorBoundaryProps {
  error: Error & { digest?: string }
  reset: () => void
  /**
   * Repo-relative path of the boundary file, e.g. `"app/host/error.tsx"`.
   * Sent to telemetry verbatim AND used to derive the console tag, so the two
   * can never disagree — the drift that let three routes log `[admin]`.
   */
  boundary: string
  /** Card heading, e.g. "Host View Error". */
  title: string
  /** One line under the heading, e.g. "Something went wrong loading the host display". */
  description: string
  /** Secondary button behaviour. Must match what the route's session allows. */
  action: RouteErrorAction
  /** Override the default reassurance line. */
  hint?: string
  /** Optional small print under the buttons. */
  footer?: string
}

const DEFAULT_HINT = "This is usually a connection blip — Try Again almost always recovers it."

/**
 * `app/admin/menu/error.tsx` → `admin/menu`; `app/error.tsx` → `app`.
 * Exported only so the console tag is asserted rather than assumed.
 */
export function segmentFor(boundary: string): string {
  return boundary.replace(/^app\/?/, "").replace(/\/?error\.tsx$/, "") || "app"
}

export function RouteErrorBoundary({
  error,
  reset,
  boundary,
  title,
  description,
  action,
  hint = DEFAULT_HINT,
  footer,
}: RouteErrorBoundaryProps) {
  useEffect(() => {
    console.error(`[${segmentFor(boundary)}] Error boundary:`, error)
    captureException(error, { boundary, digest: error.digest })
  }, [error, boundary])

  return (
    <div className="min-h-screen flex items-center justify-center p-6 bg-slate-50">
      <Card className="max-w-md w-full border-red-200 bg-red-50/50" role="alert">
        <CardHeader>
          <div className="flex items-center gap-3">
            <div className="p-2 bg-red-100 rounded-full">
              <AlertTriangle className="h-6 w-6 text-red-600" />
            </div>
            <div>
              <CardTitle className="text-red-900">{title}</CardTitle>
              <CardDescription className="text-red-700">{description}</CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Convention: never lead with raw error internals. A client-thrown
              message keeps its full text in prod (only SERVER errors are
              digest-redacted by Next), and a server mid-rush needs "tap Try
              Again", not a stack frame. The full error already went to
              captureException; the message stays reachable one tap down so
              support keeps a handle on CLIENT errors, which carry no digest. */}
          <div className="bg-red-100 p-3 rounded-lg">
            <p className="text-sm text-red-800">{hint}</p>
            {error.digest && (
              <p className="text-xs text-red-600 mt-1">Error ID: {error.digest}</p>
            )}
            {error.message && (
              <details className="mt-2">
                <summary className="text-xs text-red-600 cursor-pointer">Technical details</summary>
                <p className="text-xs font-mono text-red-800 break-words mt-1">{error.message}</p>
              </details>
            )}
          </div>
          <div className="flex gap-3">
            <Button onClick={reset} variant="outline" className="flex-1">
              <RefreshCw className="h-4 w-4 mr-2" />
              Try Again
            </Button>
            {action === "home" && (
              <Button
                onClick={() => { window.location.href = "/" }}
                variant="outline"
                className="flex-1"
              >
                <Home className="h-4 w-4 mr-2" />
                Go Home
              </Button>
            )}
            {action === "login" && (
              <Button
                onClick={() => { window.location.href = "/login" }}
                variant="outline"
                className="flex-1"
              >
                <LogIn className="h-4 w-4 mr-2" />
                Return to Login
              </Button>
            )}
            {action === "signout" && (
              <form action="/api/auth/signout" method="post" className="flex-1">
                <Button type="submit" variant="outline" className="w-full">
                  <LogOut className="h-4 w-4 mr-2" />
                  Sign Out
                </Button>
              </form>
            )}
          </div>
          {footer && <p className="text-xs text-slate-500 text-center">{footer}</p>}
        </CardContent>
      </Card>
    </div>
  )
}
